// Iter 83.5 / Phase E4 Sprint 5 — Experience Certificate print.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function ExperienceCertificatePrint() {
    const { fnfId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data: d } = await api.get(`/print/experience-certificate/${fnfId}`);
                setData(d);
            } catch (err) {
                setError(formatApiError(err, "Failed to load experience certificate"));
                toast.error(formatApiError(err, "Load failed"));
            }
        })();
    }, [fnfId]);

    if (error) return <div className="p-12 text-center text-rose-600" data-testid="experience-error">{error}</div>;
    if (!data) return <div className="p-12 text-center text-slate-400">Loading…</div>;
    const { fnf, employee, company, generated_at } = data;
    const lwd = fnf.lwd;

    return (
        <div className="bg-white min-h-screen p-14 font-serif" data-testid="experience-certificate-print">
            <style>{`@media print { @page { size: A4; margin: 14mm; } body { -webkit-print-color-adjust: exact; } }`}</style>

            <header className="border-b-4 border-slate-900 pb-4 mb-8 flex justify-between">
                <div>
                    <div className="text-xs uppercase tracking-widest text-slate-600">{company.legal_name}</div>
                    {company.address && <div className="text-[10px] text-slate-500 max-w-xs">{company.address}</div>}
                </div>
                <div className="text-right text-xs">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Date of Issue</div>
                    <div className="font-mono">{(generated_at || "").slice(0, 10)}</div>
                </div>
            </header>

            <h1 className="text-2xl font-bold text-center mb-2 uppercase tracking-widest">Experience Certificate</h1>
            <div className="text-center text-[10px] uppercase tracking-widest text-slate-500 mb-8">To Whom It May Concern</div>

            <p className="text-sm leading-relaxed mb-4">
                This is to certify that <strong>{employee?.full_name}</strong> (Employee Code:{" "}
                <span className="font-mono">{employee?.employee_code}</span>) was associated with{" "}
                <strong>{company.legal_name}</strong> from{" "}
                <strong>{employee?.date_of_joining || "—"}</strong> to <strong>{lwd}</strong>.
            </p>

            <p className="text-sm leading-relaxed mb-4">
                During the tenure with the company, <strong>{employee?.full_name}</strong> held the
                position of <strong>{employee?.designation || "—"}</strong> in the{" "}
                <strong>{employee?.department || "—"}</strong> department.
            </p>

            <p className="text-sm leading-relaxed mb-4">
                We found <strong>{(employee?.full_name || "").split(" ")[0]}</strong> to be sincere,
                hard-working, and dedicated to assigned responsibilities. They contributed positively
                to the team and demonstrated professionalism throughout their tenure.
            </p>

            <p className="text-sm leading-relaxed mb-6">
                We wish <strong>{(employee?.full_name || "").split(" ")[0]}</strong> all the best in
                their future endeavours.
            </p>

            <p className="text-sm mb-12">For <strong>{company.legal_name}</strong>,</p>

            <section className="grid grid-cols-2 gap-12">
                <div>
                    <div className="border-t border-slate-700 pt-1 text-xs font-semibold">Authorised Signatory</div>
                    <div className="text-[10px] text-slate-500 mt-1">Human Resources · Date</div>
                </div>
                <div className="text-[10px] text-slate-500 self-end">
                    <div className="font-semibold uppercase tracking-widest">Reference</div>
                    <div className="mt-0.5">FNF ID: <span className="font-mono">{fnf.id}</span></div>
                    <div>Exit Type: {fnf.exit_type}</div>
                    {fnf.certificate_id && <div>Cert: <span className="font-mono">{fnf.certificate_id}</span></div>}
                </div>
            </section>

            <footer className="mt-10 text-[10px] text-slate-500 text-center border-t border-slate-200 pt-2">
                This document is computer-generated and traceable to FNF{" "}
                <span className="font-mono">{fnf.id}</span> issued by {company.legal_name}.
            </footer>
        </div>
    );
}
